import { WKButton, WKModal } from '@wk/components';
import { trace, traceEvent } from '@wk/wk-gatherer';
import cls from 'classnames';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Cookie from 'js-cookie';
import { isEmpty } from 'lodash';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Tooltip from 'rc-tooltip';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import clockIcon from '@public/imgs/clock-icon-new.svg';
import cogniaIcon from '@public/imgs/icon-cognia.svg';
import timeIcon from '@public/imgs/icon-course.svg';
import learnIcon from '@public/imgs/icon-learn-center.svg';
import exitIcon from '@public/imgs/icon-logout.svg';
import passwordIcon from '@public/imgs/icon-password.svg';
import personIcon from '@public/imgs/icon-profile.svg';
import subscriptionIcon from '@public/imgs/icon-subscription.svg';
import orderIcon from '@public/imgs/order.svg';
import arrowBottomIcom from '@public/imgs/pc/arrow-bottom-icon.svg';
import backIcon from '@public/imgs/pc/icon-back.svg';
import leaveIcon from '@public/imgs/pc/icon-leave.svg';
import languageIcon from '@public/imgs/pc/language-icon.svg';
import voucherIcon from '@public/imgs/voucher.svg';

import CalendarTip from '../CalendarTip';
import ChangePwd from '../ChangePwd';
import MobileHeader from '../MobileHeader';
import User from '../User';

import styles from './style.module.scss';
import SwitchVersionHint from './SwitchVersionHint';
import SwitchVersionModal from './SwitchVersionModal';

import { updateVersionState } from '@/api/common';
import { log } from '@/api/log';
import { getUnReadMessageNum } from '@/api/message';
import { reserveSubject } from '@/api/reserve';
import { StudentInfo, SubscriptionEsRes } from '@/api/types/home';
import { GetUnReadMessageNumRes } from '@/api/types/message';
import { Dropdown } from '@/components';
import { STUDENT_LIST } from '@/pageComponents/Profile/Info';
import { CANCEL_COURSE_STEP } from '@/pages/cancel-course';
import useNews from '@/store/useNews';
import useRenewal from '@/store/useRenewal';
import useUser from '@/store/useUser';
import cacheStorage from '@/utils/cacheStorage';
import {
  CURRENT_LANGUAGE,
  LanguageList,
  SHOW_SWITCH_VERSION_PATHS,
} from '@/utils/constants';
import { generateNamePrefix } from '@/utils/helpers';
import useDeviceType from '@/utils/hooks/useDeviceType';
import useTraceEventWithAccount from '@/utils/hooks/useTraceEventWithAccount';
import PATHS from '@/utils/paths';
import { switchVersion } from '@/utils/url';
import { isTourist } from '@/utils/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

interface HeaderProps {
  canBack: boolean;
  timezone?: string;
  timezoneStd?: string;
  onLogout: (routerParams?: string) => void;
  curStudent: StudentInfo | null;
  students: StudentInfo[] | null;
  setCurStudent: (data: StudentInfo) => void;
  showStudentCenter?: boolean;
  showReserveModal?: boolean;
  showHeaderBack?: boolean;
  canSwitchStudent?: boolean;
  handleCustomBack?: ((_: any) => void) | null | undefined;
  showLanguageSwitching?: boolean;
  showTools?: boolean;
  showLanguageDrop?: boolean;
  hiddenTimezone?: boolean;
  timezoneCanClick?: boolean;
  stuScheduleContent?: SubscriptionEsRes | null;
  calendarTip?: boolean;
  closeCalendarTip?: (visible: boolean) => void;
}

const prefixName = generateNamePrefix('home.');

const Header: React.FC<HeaderProps> = props => {
  const {
    canBack,
    timezoneStd = '',
    onLogout,
    curStudent,
    students,
    showStudentCenter,
    showReserveModal,
    setCurStudent,
    showHeaderBack,
    handleCustomBack,
    canSwitchStudent = false,
    showLanguageSwitching = true,
    showTools = true,
    showLanguageDrop = true,
    stuScheduleContent,
    hiddenTimezone = false,
    timezoneCanClick = true,
    calendarTip = false,
    closeCalendarTip,
    ...restProps
  } = props || {};

  const { account } = useUser();
  const router = useRouter();
  const { t, i18n } = useTranslation('home');
  const { t: appointmentT } = useTranslation('appointment');
  const { t: commonT } = useTranslation('common');
  const [pwdVisible, setPwdVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { traceEventWithAccount } = useTraceEventWithAccount();
  const isMobile = useDeviceType();
  const renewal = useRenewal();
  const switchButtonRef = useRef<HTMLDivElement>(null);

  const { setUnReadMessageInfo } = useNews();
  const [showSwitchVersionModal, setShowSwitchVersionModal] = useState(false);
  const [showSwitchVersionHint, setShowSwitchVersionHint] = useState(false);

  useEffect(() => {
    if (account?.accountData?.webEventNotice?.WEB_FLOAT_NOTICE) {
      setShowSwitchVersionHint(true);
    }
  }, [account]);

  useUpdateEffect(() => {
    if (
      !!students?.length &&
      !router.pathname.split('/').includes('cashier-desk')
    ) {
      getUnReadMessageNum().then((res: GetUnReadMessageNumRes['content']) => {
        setUnReadMessageInfo(res);
      });
    }
  }, [students]);

  const {
    subject,
    source,
    showCalendarModal: qShowCalendarModal,
  } = useRouter().query;

  const memorizedHandleModalClose = useCallback(() => {
    setShowModal(false);
    traceEvent({
      data: {
        _event: 'U_BookingPop_KeeponClick',
        subject: subject
          ? subject
          : window.sessionStorage.getItem('curSubject'),
        account_id: account?.uuid,
        booking_triggerevent: source
          ? 'web_official'
          : router.query?.booking_triggerevent,
        trigger_pvid: Cookie.get('trigger_pvid'),
        login_triggerevent: Cookie.get('login_triggerevent'),
        login_method: Cookie.get('login_type'),
      },
    });
  }, [account?.uuid, router.query.booking_triggerevent, source, subject]);

  const handleChangePwd = () => {
    setPwdVisible(true);
  };

  const handleBack = () => {
    sessionStorage.removeItem(CANCEL_COURSE_STEP);
    if (showReserveModal) {
      setShowModal(true);
    } else {
      router.replace(PATHS.home).then();
    }
  };

  const handleBackCustom = (e: any) => {
    if (handleCustomBack) {
      handleCustomBack(e);
    } else {
      router.back();
    }
  };

  const onSwitchStudent = (student: StudentInfo) => {
    setCurStudent(student);
    const NEED_REFRESH_ROUTES = [PATHS.playback];
    const { pathname } = router;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    NEED_REFRESH_ROUTES?.includes(pathname) && router.replace(PATHS.curriculum);
  };

  const handleToSubscriptionDetail = () => {
    const data = {
      _event: 'U_HP_SubscribeEnterClick',
      teacherId: '',
      courseStatus: '',
      studentType: 'GS2.0',
    };
    traceEvent({
      data,
    });
    router.push({
      pathname: `/home/subscriptionDetails`,
      query: { triggerEvent: 'U_HP_SubscribeEnterClick' },
    });
  };

  const handleToNewVersion = async () => {
    trace('C_VersionChange', {});
    if (showSwitchVersionHint) {
      setShowSwitchVersionHint(false);
    }

    const { WEB_HOMEPAGE_NOTICE } = account?.accountData?.webEventNotice || {};
    const isHomePage = router.pathname === PATHS.home;
    if (isHomePage && WEB_HOMEPAGE_NOTICE) {
      await updateVersionState({ events: [{ type: 'WEB_FLOAT_NOTICE' }] });
      setShowSwitchVersionModal(true);
      return;
    }
    await updateVersionState({
      events: [
        { type: 'WEB_FLOAT_NOTICE' },
        { type: 'WEB_VERSION_CHOICE', choice: 'NEW' },
      ],
    });
    switchVersion();
  };

  if (isMobile && router.pathname.split('/').includes('cashier-desk')) {
    return <MobileHeader onLogout={onLogout} />;
  }

  return (
    <>
      <CalendarTip
        visible={calendarTip}
        close={closeCalendarTip}
        showCalendarModal={Boolean(qShowCalendarModal)}
      />
      <div className={styles.headerWrapper} id="home-layout-header">
        <section className={styles.leftContainer}>
          {showStudentCenter && (
            <div
              style={{ opacity: canBack ? '1' : '.3' }}
              className={styles.back}>
              <div role="button" onClick={canBack ? handleBack : undefined}>
                {/* <Image src={backIcon} /> */}
                {/* <span>{t('学员中心')}</span> */}
              </div>
            </div>
          )}
          {showHeaderBack && (
            <div className={styles.back}>
              <div role="button" onClick={handleBackCustom}>
                <Image src={backIcon} alt="WK" />
              </div>
            </div>
          )}
          {pwdVisible && (
            <ChangePwd
              visible={pwdVisible}
              onChangeVisible={setPwdVisible}
              curStudent={curStudent}
              onLogout={onLogout}
            />
          )}
          {!isTourist() && timezoneStd && !hiddenTimezone && (
            <div
              role="button"
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: timezoneCanClick ? 'pointer' : 'unset',
              }}
              onClick={() => {
                if (!timezoneCanClick) {
                  return;
                }
                traceEventWithAccount({
                  _event: 'U_PC_TimeAreaClick',
                });
                router.push({
                  pathname: PATHS.profile,
                  query: { anchor: STUDENT_LIST },
                });
              }}>
              <div className={styles.icon}>
                <Image src={clockIcon} alt="WK" />
              </div>
              <span
                className={styles.timezone}>{`${restProps.timezone?.substring(
                0,
                restProps?.timezone?.indexOf(')') + 1,
              )} ${timezoneStd}`}</span>
            </div>
          )}
        </section>
        {showLanguageSwitching && (
          <section className={styles.rightContainer}>
            {router.pathname !== '/home/renewalCourses' &&
              stuScheduleContent?.display && (
                <div
                  className={styles.subscribeBtn}
                  onClick={handleToSubscriptionDetail}>
                  {i18n.language === 'zh' ? '订阅' : 'Subscribe'}
                </div>
              )}
            {/* 优惠 */}
            {router.pathname === '/home' &&
              !isEmpty(renewal?.homeRenewalEntry?.normalEntry) && (
                <button
                  className="mr-[20px] flex h-[32px] cursor-pointer items-center justify-center rounded-[50px] border-transparent bg-[#CD292A] px-[12px] text-[14px] font-extrabold leading-normal text-white"
                  onClick={() => {
                    const data = renewal?.homeRenewalEntry?.normalEntry;
                    const eventData = {
                      data: {
                        _event: 'C_MKActivity_Entrance',
                        activityConfig: {
                          activityEnName: data?.activityEnName || '',
                          activityZhName: data?.activityZhName || '',
                          activityId: data?.activityId || '',
                          startTime: data?.startTime || '',
                          expireTime: data?.expireTime || '',
                          activityType: data?.activityType || '',
                          activityStatus: data?.activityStatus || '',
                          subject: data?.subjects || '',
                        },
                        studentType: renewal.studentType,
                        dsSource: 'Header',
                      },
                    };
                    traceEvent(eventData);
                    console.log('>>>C_MKActivity_Entrance', eventData);
                    router.push({
                      pathname: `/home/renewalCourses`,
                      query: {
                        activityId:
                          renewal?.homeRenewalEntry?.normalEntry?.activityId,
                      },
                    });
                  }}>
                  <svg
                    className="mr-[4px]"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none">
                    <path
                      d="M7.76074 0.623535C8.83274 0.567535 9.8816 0.97584 10.6416 1.73584L14.2734 5.35205C15.7613 6.84806 15.7614 9.2645 14.2734 10.7524L10.7607 14.2642C9.26477 15.7518 6.84924 15.7519 5.36133 14.2642L1.7373 10.6401C0.977413 9.88025 0.569324 8.83228 0.617188 7.75244L0.80957 3.75244C0.889563 2.16058 2.16095 0.888128 3.76074 0.815918L7.76074 0.623535ZM12.4268 8.37549C12.1947 8.14416 11.8109 8.14382 11.5791 8.37549L8.37891 11.5757C8.14731 11.8075 8.1476 12.1913 8.37891 12.4233C8.49869 12.5431 8.65101 12.5999 8.80273 12.6001C8.95468 12.6001 9.10756 12.5433 9.22754 12.4233L12.4268 9.22412C12.6588 8.99212 12.6588 8.60749 12.4268 8.37549ZM6.00293 3.6958C4.73088 3.69606 3.69953 4.72747 3.69922 5.99951C3.69922 7.27181 4.73069 8.30394 6.00293 8.3042C7.27535 8.30414 8.30664 7.27194 8.30664 5.99951C8.30633 4.72734 7.27516 3.69586 6.00293 3.6958Z"
                      fill="#FFE395"
                    />
                  </svg>
                  {i18n.language === 'zh' ? '优惠' : 'Save Off'}
                </button>
              )}
            {SHOW_SWITCH_VERSION_PATHS.includes(router.pathname) && (
              <Tooltip
                placement="bottom"
                overlay={commonT('版本切换-按钮-hover信息')}>
                <div
                  ref={switchButtonRef}
                  className={cls(
                    'relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-[#F7F7F7]',
                    canSwitchStudent && 'mr-[20px]',
                  )}
                  onClick={handleToNewVersion}>
                  <Image
                    width={24}
                    height={24}
                    src="/icon/switch.png"
                    alt="Wukong"
                    className="h-6 w-6"
                  />
                </div>
              </Tooltip>
            )}
            {canSwitchStudent && (
              <User
                curStudent={curStudent}
                students={students}
                onSwitch={onSwitchStudent}
              />
            )}
            {showLanguageDrop && (
              <Dropdown
                optionClassName={styles.dropdownContainer}
                optionChildren={
                  <div className={styles.languageBox}>
                    {LanguageList?.map(({ label, value }) => (
                      <div
                        key={value}
                        role="button"
                        className={cls(
                          styles.languageItem,
                          i18n.language === value && styles.languageItemActive,
                        )}
                        onClick={() => {
                          if (i18n.language !== value) {
                            traceEventWithAccount({
                              _event: 'U_PC_LanguageClick',
                              language: value,
                            });
                            cacheStorage.setItem(CURRENT_LANGUAGE, value);
                            router.replace(router.asPath, router.asPath, {
                              locale: value,
                            });
                          }
                        }}>
                        {label}
                      </div>
                    ))}
                  </div>
                }>
                <div className={styles.languageContainer}>
                  <span className={styles.languageIcon}>
                    <Image src={languageIcon} alt="WK" />
                  </span>
                  {LanguageList?.find(({ value }) => value === i18n.language)
                    ?.label || ''}
                  <span className={styles.arrowIcon}>
                    <Image src={arrowBottomIcom} alt="WK" />
                  </span>
                </div>
              </Dropdown>
            )}
            {/* <div
        onClick={() => toMessagePage()}
        className="ml-[20px] bg-[#f7f7f7] rounded-[50%] flex items-center justify-center cursor-pointer w-[32px] relative"
      >
        {!!unReadMessageInfo && <Bell />}
        {(unReadMessageInfo?.courseCount > 0 || unReadMessageInfo?.systemCount > 0) && (
          <RedPoint className="absolute top-[5px] right-[6px]" />
        )}
      </div> */}
            {showTools && (
              <Dropdown
                className={styles.person}
                optionClassName={cls(styles.dropdownContainer, styles.misc)}
                optionChildren={
                  <>
                    {!isTourist() && (
                      <>
                        <Link href={{ pathname: PATHS.profile }}>
                          <div
                            className={styles.personItem}
                            role="button"
                            onClick={() => {
                              traceEventWithAccount({
                                _event: 'U_PC_PersonalCenterClick',
                              });
                              log(prefixName`enter_profile`);
                            }}>
                            <span className={styles.icon}>
                              <Image src={personIcon} alt="WK" />
                            </span>
                            <span className={styles.title}>
                              {t('个人中心')}
                            </span>
                          </div>
                        </Link>
                        <Link href={{ pathname: PATHS.learnCenter }}>
                          <div
                            className={styles.personItem}
                            role="button"
                            onClick={() => {
                              traceEventWithAccount({
                                _event: 'U_HP_StudyCenterEnterClick',
                                // subject: learnSituationData?.map(({ subject }) => subject)?.join(','),
                                // level1: learnSituationData?.map(({ level1 }) => level1)?.join(','),
                              });
                            }}>
                            <span className={styles.icon}>
                              <Image src={learnIcon} alt="WK" />
                            </span>
                            <span className={styles.title}>
                              {t('学情中心')}
                            </span>
                          </div>
                        </Link>
                        <Link href={{ pathname: PATHS.period }}>
                          <div
                            className={styles.personItem}
                            role="button"
                            onClick={() => log(prefixName`enter_period`)}>
                            <span className={styles.icon}>
                              <Image src={timeIcon} alt="WK" />
                            </span>
                            <span className={styles.title}>
                              {t('课时信息')}
                            </span>
                          </div>
                        </Link>
                        <Link href={{ pathname: PATHS.vouchers }}>
                          <div
                            className={styles.personItem}
                            role="button"
                            onClick={() => {
                              traceEvent({
                                data: {
                                  _event: 'U_HomePage_VoucherClick',
                                  Code: curStudent?.code,
                                },
                              });
                              log(prefixName`enter_period`);
                            }}>
                            <span className={styles.icon}>
                              <Image src={voucherIcon} alt="WK" />
                            </span>
                            <span className={styles.title}>
                              {t('我的代金券')}
                            </span>
                          </div>
                        </Link>
                        <Link href={{ pathname: PATHS.orderCenter }}>
                          <div
                            className={styles.personItem}
                            role="button"
                            onClick={() => {
                              traceEvent({
                                data: {
                                  _event: 'U_HomePage_OderCenterClick',
                                  Code: curStudent?.code,
                                },
                              });
                              log(prefixName`enter_period`);
                            }}>
                            <span className={styles.icon}>
                              <Image src={orderIcon} alt="WK" />
                            </span>
                            <span className={styles.title}>
                              {t('我的订单')}
                            </span>
                          </div>
                        </Link>
                        <Link href={{ pathname: PATHS.subscription }}>
                          <div
                            className={styles.personItem}
                            role="button"
                            onClick={() => {
                              traceEvent({
                                data: {
                                  _event: 'U_HomePage_MySubscriptionClick',
                                  Code: curStudent?.code,
                                },
                              });
                            }}>
                            <span className={styles.icon}>
                              <Image src={subscriptionIcon} alt="WK" />
                            </span>
                            <span className={styles.title}>
                              {t('我的订阅')}
                            </span>
                          </div>
                        </Link>
                        <Link href={{ pathname: PATHS.cognia }}>
                          <div
                            className={styles.personItem}
                            role="button"
                            onClick={() => {
                              traceEventWithAccount({
                                _event: 'U_PersonalCenter_CogniaApplyClick',
                              });
                            }}>
                            <span className={styles.icon}>
                              <Image src={cogniaIcon} alt="WK" />
                            </span>
                            <span className={styles.title}>
                              {t('申请Cognia证书')}
                            </span>
                          </div>
                        </Link>
                        <div
                          className={styles.personItem}
                          role="button"
                          onClick={handleChangePwd}>
                          <span className={styles.icon}>
                            <Image src={passwordIcon} alt="WK" />
                          </span>
                          <span className={styles.title}>{t('修改密码')}</span>
                        </div>
                        <Link href={{ pathname: PATHS.leave }}>
                          <div className={styles.personItem} role="button">
                            <span className={styles.icon}>
                              <Image src={leaveIcon} alt="WK" />
                            </span>
                            <span className={styles.title}>
                              {t('请假记录')}
                            </span>
                          </div>
                        </Link>
                      </>
                    )}
                    <div
                      className={styles.personItem}
                      role="button"
                      onClick={() => {
                        log(prefixName`logout`);
                        onLogout();
                      }}>
                      <span className={styles.icon}>
                        <Image src={exitIcon} alt="WK" />
                      </span>
                      <span className={styles.title}>{t('退出登录')}</span>
                    </div>
                  </>
                }>
                <span className={styles.account} />
              </Dropdown>
            )}
          </section>
        )}
        <WKModal
          showClose={false}
          open={showModal}
          onCancel={memorizedHandleModalClose}>
          {/* <div className={styles.modalContent}>
        <p className={styles.modalTitle}>约课遇到问题?</p>
        <p className={styles.modalSub}>扫码添加专属学习规划师，为您解决问题</p>
        <div className={styles.qrcodeBox}>
          <div className={styles.qrItem}>
            <div className={styles.code}>
              <Image src={whatsAppsImg} alt="WhatsApp" layout="responsive" />
            </div>
            <span>{t('自主化-上课须知-添加学习规划师-whatsApp')}</span>
          </div>
          <div className={styles.qrItem}>
            <div className={styles.code}>
              <Image src={wechatImg} alt="WeChat" layout="responsive" />
            </div>
            <span>{t('自主化-上课须知-添加学习规划师-wechat')}</span>
          </div>
        </div>
        <WKButton type={'primary'} className={styles.continueAppointBtn} onClick={memorizedHandleModalClose}>
          {appointmentT('继续约课')}
        </WKButton>
        <WKButton
          type={'text'}
          style={{ width: '100%', fontSize: '14px' }}
          onClick={() => {
            const reserveForm = window.sessionStorage.getItem('ReserveForm')
              ? JSON.parse(window.sessionStorage.getItem('ReserveForm') ?? '')
              : null;
            const params =
              reserveForm?.subject === 'math'
                ? { grade: `${reserveForm?.grade}` }
                : { chineseLevel: `${reserveForm?.grade}` };
            traceEvent({
              data: {
                _event: 'U_BookingPop_GaveUpClick',
                subject: subject ? subject : window.sessionStorage.getItem('curSubject'),
                account_id: account?.uuid,
                booking_triggerevent: source ? 'web_official' : router.query?.booking_triggerevent,
                trigger_pvid: Cookie.get('trigger_pvid'),
                login_triggerevent: Cookie.get('login_triggerevent'),
                login_method: Cookie.get('login_type'),
              },
            });
            if (curStudent && reserveForm?.grade) {
              reserveSubject({
                subject: reserveForm?.subject?.toUpperCase(),
                info: {
                  timeZoneStandard: reserveForm?.timezone,
                  age: reserveForm?.age,
                  country: reserveForm?.country,
                  courseDate: reserveForm?.date,
                  courseTime: reserveForm?.time,
                  firstName: reserveForm?.name,
                  ...params,
                },
              } as any).then();
            }
            setShowModal(false);
            router.push(PATHS.home).then();
          }}
        >
          {appointmentT('放弃免费约课机会')}
        </WKButton>
      </div> */}
          <div className={styles.modalSimpleContent}>
            <p className={styles.modalTitle}>
              {appointmentT('确定要放弃约课吗')}
            </p>
            <p className={styles.modalSub}>
              {appointmentT('退出信息将不会保存')}
            </p>
            <WKButton
              type="primary"
              className={styles.continueAppointBtn}
              onClick={memorizedHandleModalClose}>
              {appointmentT('继续约课')}
            </WKButton>
            <WKButton
              type="text"
              style={{ width: '100%', fontSize: '14px', marginTop: '13px' }}
              onClick={() => {
                const reserveForm = window.sessionStorage.getItem('ReserveForm')
                  ? JSON.parse(
                      window.sessionStorage.getItem('ReserveForm') ?? '',
                    )
                  : null;
                const params =
                  reserveForm?.subject === 'math'
                    ? { grade: `${reserveForm?.grade}` }
                    : { chineseLevel: `${reserveForm?.grade}` };
                traceEvent({
                  data: {
                    _event: 'U_BookingPop_GaveUpClick',
                    subject: subject
                      ? subject
                      : window.sessionStorage.getItem('curSubject'),
                    account_id: account?.uuid,
                    booking_triggerevent: source
                      ? 'web_official'
                      : router.query?.booking_triggerevent,
                    trigger_pvid: Cookie.get('trigger_pvid'),
                    login_triggerevent: Cookie.get('login_triggerevent'),
                    login_method: Cookie.get('login_type'),
                  },
                });
                if (curStudent && reserveForm?.grade) {
                  reserveSubject({
                    subject: reserveForm?.subject?.toUpperCase(),
                    info: {
                      timeZoneStandard: reserveForm?.timezone,
                      age: reserveForm?.age,
                      country: reserveForm?.country,
                      courseDate: reserveForm?.date,
                      courseTime: reserveForm?.time,
                      firstName: reserveForm?.name,
                      ...params,
                    },
                  } as any).then();
                }
                setShowModal(false);
                router.push(PATHS.home).then();
              }}>
              {appointmentT('放弃免费约课机会')}
            </WKButton>
          </div>
        </WKModal>
        {SHOW_SWITCH_VERSION_PATHS.includes(router.pathname) && (
          <>
            <SwitchVersionHint
              visible={showSwitchVersionHint}
              setVisible={setShowSwitchVersionHint}
              buttonRef={switchButtonRef}
            />
            <SwitchVersionModal
              visible={showSwitchVersionModal}
              setVisible={setShowSwitchVersionModal}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Header;
